class AtheneumContactsHelper {
  constructor(atheneum) {
    this.Atheneum = atheneum;
    this.timestamp = undefined;
    this.contactsLoaded = false;
    this.caching = false;
    this.totalContacts = 0;
    this.forceRecount = false;

    const atheneumContacts = this;

    // Determine the current search configuration from theme settings.
    this.allowSearchTokens = false; //Needs more work  this.Atheneum.Theme.getBooleanProperty('allowSearchTokensForDB', false);
    this.allowFuzzySearchTerms = false; //Needs more work. this.Atheneum.Theme.getBooleanProperty('allowFuzzySearchTerms', false);

    this.tokenConfig = "none";
    if (this.allowSearchTokens) {
      this.tokenConfig = this.allowFuzzySearchTerms ? "sub_fuzzy" : "sub";
    }

    const dbName = `AtheneumContactsHelper_${this.tokenConfig}`;
    this.timestampKey = `atheneum_contacts_timestamp_${this.tokenConfig}`;
    this.totalContactsKey = `atheneum_total_contacts_cache_${this.tokenConfig}`;
    this.userIdKey = `atheneum_contacts_user_id_${this.tokenConfig}`;
    this.dbName = dbName;

    this.fieldMap = {};
    this.fieldMap["id"] = "id";
    this.fieldMap["email"] = "email";
    this.fieldMap["firstname"] = "info_firstName";
    this.fieldMap["lastname"] = "info_lastName";
    this.fieldMap["country"] = "info_country";
    this.fieldMap["to"] = "info_to";
    this.fieldMap["collectionid"] = "collection_id";
    this.fieldMap["totalsubcount"] = "totalFormSubmissions";
    this.fieldMap["repsubmitted"] = "repSubmitted";
    this.fieldMap["consentstatus"] = "status";
    this.fieldMap["externalid"] = "externalId";

    this.normalizedFieldMap = {};
    this.normalizedFieldMap["email"] = "email_normalized";
    this.normalizedFieldMap["firstname"] = "info_firstName_normalized";
    this.normalizedFieldMap["lastname"] = "info_lastName_normalized";
    this.normalizedFieldMap["country"] = "info_country_normalized";
    this.normalizedFieldMap["to"] = "info_to_normalized";

    this._initDB();

    this.__contactPageSize = atheneumContacts.Atheneum.Theme.getIntegerProperty(
      "contactsPerPage",
      "10000",
    );
    this.__proxyContactPageSize = 250;
    this.__delayBetweenContactPageRequests = 0;
    this.__proxyDelayBetweenContactPageRequests = 0;
    this.__abortAnyCacheAttemptStartedBefore = undefined;
    this.__preferProxyQueryToCache = false;
    this.__preferLiveQueryToCache = false;
  }

  _initDB() {
    const customIndexes = this.Atheneum.Theme.getStringProperty(
      "customIndexes",
      "",
    );
    const dynamicDexieIndexes = [];
    const dynamicNormalizedDexieIndexes = [];
    this.dynamicCompoundIndexes = [];

    if (customIndexes) {
      const indexesArray = customIndexes.split(",");
      indexesArray.forEach((index) => {
        const trimmedIndex = index.trim();
        if (!trimmedIndex) return;

        if (trimmedIndex.startsWith("[") && trimmedIndex.endsWith("]")) {
          dynamicDexieIndexes.push(trimmedIndex);

          const fields = trimmedIndex.slice(1, -1).split("+");
          const simpleFieldNames = fields.map((f) =>
            f.replace(/^(info_|ext_)/, "").replace(/_normalized$/, ""),
          );
          const name = simpleFieldNames
            .map((f) => f.charAt(0).toUpperCase() + f.slice(1))
            .join("And");

          this.dynamicCompoundIndexes.push({
            name: name,
            fields: simpleFieldNames,
            dexieName: trimmedIndex,
          });
        } else {
          if (trimmedIndex.startsWith("info_")) {
            const key = trimmedIndex.substring(5);
            this.fieldMap[key] = trimmedIndex;
            dynamicDexieIndexes.push(trimmedIndex);
            this.normalizedFieldMap[key] = `${trimmedIndex}_normalized`;
            dynamicNormalizedDexieIndexes.push(`${trimmedIndex}_normalized`);
          } else {
            this.fieldMap[trimmedIndex] = trimmedIndex;
            dynamicDexieIndexes.push(trimmedIndex);
            this.normalizedFieldMap[trimmedIndex] =
              `${trimmedIndex}_normalized`;
            dynamicNormalizedDexieIndexes.push(`${trimmedIndex}_normalized`);
          }
        }
      });
    }

    if (window.Dexie) {
      this.db = new Dexie(this.dbName); // Use the mode-specific name

      let baseIndexes = [
        "id",
        "email_normalized",
        "externalId",
        "test",
        "collection_id",
        "[info_lastName_normalized+info_firstName_normalized]",
        "info_firstName_normalized",
        "info_lastName_normalized",
        "info_country_normalized",
        "info_to_normalized",
      ];

      if (
        this.Atheneum.Theme.getBooleanProperty(
          "useCountryNameComboIndexes",
          false,
        )
      ) {
        baseIndexes.push(
          "[info_lastName_normalized+info_firstName_normalized+info_country_normalized]",
          "[info_firstName_normalized+info_country_normalized]",
          "[info_lastName_normalized+info_country_normalized]",
        );
      }

      if (this.allowSearchTokens) {
        baseIndexes.push("_search_tokens");
      }

      if (
        !this.Atheneum.Theme.getBooleanProperty(
          "disableFeatureContactsScreen",
          false,
        )
      ) {
        baseIndexes.push(
          "totalFormSubmissions",
          "status",
          "repSubmitted",
          "info_consented",
        );
      }

      const allDexieIndexes = [
        ...new Set([
          ...baseIndexes,
          ...dynamicDexieIndexes,
          ...dynamicNormalizedDexieIndexes,
        ]),
      ];
      const schemaString = allDexieIndexes.join(",\n");

      this.db.version(1).stores({
        // Version is now 1 for each DB, as they are separate.
        contacts: schemaString,
      });
    }
  }

  /**
   * Initializes the contact helper by cleaning up old, unused databases.
   * This MUST be async to prevent race conditions.
   */
  async initializeAndValidateCache() {
    if (!this.db) return;

    // --- Background cleanup of old databases ---
    const allModes = ["none", "sub", "sub_fuzzy"];
    const modesToDelete = allModes.filter((m) => m !== this.tokenConfig);

    const deletePromises = [];

    for (const mode of modesToDelete) {
      const oldDbName = `AtheneumContactsHelper_${mode}`;

      // Add the delete promise to an array
      deletePromises.push(
        Dexie.delete(oldDbName).catch((err) =>
          console.error(`Failed to delete old DB: ${oldDbName}`, err),
        ),
      );

      localStorage.removeItem(`atheneum_contacts_timestamp_${mode}`);
      localStorage.removeItem(`atheneum_total_contacts_cache_${mode}`);
      localStorage.removeItem(`atheneum_contacts_user_id_${mode}`);
    }

    await Promise.all(deletePromises);

    try {
      this.__delayBetweenContactPageRequests =
        this.Atheneum.Theme.getIntegerProperty(
          "delayBetweenContactPageRequests",
          0,
        );
      if (this.db) {
        await this.db.open();
      }
    } catch (err) {
      console.error("Error explicitly opening database after cleanup:", err);
    }
  }

  /**
   * Generates fuzzy search candidates for a word (deletions and transpositions).
   * @param {string} word The word to generate candidates for.
   * @returns {Array<string>} An array of fuzzy candidates.
   * @private
   */
  _generateFuzzyCandidates(word) {
    if (!word) {
      return [];
    }

    const sanitizedWord = word.toLowerCase();
    const candidates = new Set([sanitizedWord]);

    // 1. Deletions: Create candidates by removing one character at a time.
    for (let i = 0; i < sanitizedWord.length; i++) {
      candidates.add(sanitizedWord.slice(0, i) + sanitizedWord.slice(i + 1));
    }

    // 2. Transpositions: Create candidates by swapping adjacent characters.
    for (let i = 0; i < sanitizedWord.length - 1; i++) {
      candidates.add(
        sanitizedWord.slice(0, i) +
          sanitizedWord[i + 1] +
          sanitizedWord[i] +
          sanitizedWord.slice(i + 2),
      );
    }

    return Array.from(candidates);
  }

  /**
   * Creates all search tokens for a given text, conditionally adding fuzzy candidates.
   * @param {string} text The string to create tokens from.
   * @returns {Array<string>}
   * @private
   */
  _createSearchTokens(text) {
    if (!text || typeof text !== "string") return [];

    if (this.allowFuzzySearchTerms) {
      // If full fuzzy search is on, we only need the candidates (which include the original word).
      return this._generateFuzzyCandidates(text);
    } else {
      // Otherwise, for a "contains" search, generate substrings (n-grams).
      const normalized = this._normalizeStringForSearch(text);
      const tokens = new Set([normalized]);

      // Generate n-grams (substrings) of length 2 and up
      for (let i = 0; i < normalized.length; i++) {
        for (let j = i + 2; j <= normalized.length; j++) {
          tokens.add(normalized.substring(i, j));
        }
      }
      return Array.from(tokens);
    }
  }

  /**
   * Helper to flatten a contact object before storing it in Dexie.
   * Also generates and adds search tokens for fuzzy matching if enabled.
   */
  processContactForStorage(contactData) {
    const flatData = { ...contactData };

    if (flatData.info && typeof flatData.info === "object") {
      for (const key in flatData.info) {
        if (Object.prototype.hasOwnProperty.call(flatData.info, key)) {
          const flattenedKey = "info_" + key;
          flatData[flattenedKey] = flatData.info[key];
        }
      }
      if (Object.prototype.hasOwnProperty.call(flatData.info, "consented")) {
        flatData.info_consented = flatData.info.consented;
      }
      delete flatData.info;
    }

    if (flatData.collection && flatData.collection.id) {
      flatData.collection_id = flatData.collection.id;
      delete flatData.collection;
    }

    // Create normalized fields for exact matching and sorting
    for (const key in this.normalizedFieldMap) {
      const originalDbFieldName =
        this.fieldMap[key.toLowerCase()] || key.toLowerCase();
      const normalizedDbFieldName = this.normalizedFieldMap[key.toLowerCase()];
      const originalValue = flatData[originalDbFieldName];
      flatData[normalizedDbFieldName] =
        this._normalizeStringForSearch(originalValue);
    }

    // Generate and add search tokens if enabled via theme
    if (this.allowSearchTokens) {
      const allTokens = new Set();
      const fieldsToTokenize = [
        "info_firstName",
        "info_lastName",
        "email",
        "info_country",
      ];
      fieldsToTokenize.forEach((field) => {
        const value = flatData[field];
        if (value) {
          this._createSearchTokens(value).forEach((token) =>
            allTokens.add(token),
          );
        }
      });
      flatData._search_tokens = Array.from(allTokens);
    }

    return flatData;
  }

  /**
   * Helper to unflatten a contact object retrieved from Dexie.
   * Removes temporary fields like search tokens.
   */
  processContactForRetrieval(flatContactData) {
    const unflatData = { ...flatContactData };
    unflatData.info = {};

    for (const key in unflatData) {
      if (Object.prototype.hasOwnProperty.call(unflatData, key)) {
        if (key.endsWith("_normalized") || key === "_search_tokens") {
          delete unflatData[key];
          continue;
        }

        if (key.startsWith("info_")) {
          const originalKey = key.substring(5);
          unflatData.info[originalKey] = unflatData[key];
          delete unflatData[key];
        }
      }
    }

    if (Object.prototype.hasOwnProperty.call(unflatData, "collection_id")) {
      unflatData.collection = { id: unflatData.collection_id };
      delete unflatData.collection_id;
    }

    if (Object.prototype.hasOwnProperty.call(unflatData, "info_consented")) {
      delete unflatData.info_consented;
    }
    return unflatData;
  }

  /**
   * Normalizes a string for case-insensitive and diacritic-insensitive search.
   */
  _normalizeStringForSearch(str) {
    if (typeof str !== "string") return "";
    return str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  }

  /**
   * Generates a comma-separated list of custom fields from a form.
   */
  getCustomFieldListForForm(form) {
    const fieldNames = [];
    const typeIgnoreList = this.Atheneum.Theme.getStringProperty(
      "customFieldListIgnoreTypeList",
      "HIDDEN,SIGNATURE,LABEL,FILE,SIGNATURE,SELECT,RADIO,RADIO_MULTI,CONSENT_TYPE,CONFIRM",
    ).split(",");

    for (const field of form.fields) {
      if (typeIgnoreList.includes(field.type)) {
        continue;
      }
      fieldNames.push(
        this.Atheneum.Theme.getStringProperty(
          "customFieldNameConversion_" + field.name,
          field.name,
        ),
      );
    }
    return fieldNames.join(",");
  }

  async clearCache(remakeDB = false) {
    this.totalContacts = 0;
    this.timestamp = undefined;
    this.forceRecount = true;
    // Use mode-specific keys
    localStorage.removeItem(this.totalContactsKey);
    localStorage.removeItem(this.userIdKey);
    localStorage.removeItem(this.timestampKey);
    this.__abortAnyCacheAttemptStartedBefore = new Date().getTime();

    if (this.db) {
      const dbNameToDelete = this.dbName;
      if (this.db.isOpen()) {
        this.db.close();
      }

      this.db = undefined;
      this.dbName = undefined;

      if (dbNameToDelete) {
        await Dexie.delete(dbNameToDelete).catch((err) =>
          console.error(`Failed to delete old DB: ${dbNameToDelete}`, err),
        );
      }
    }

    if (remakeDB) this._initDB();
  }

  async getTotalContacts(options = {}) {
    if (!this.db) return 0;

    if (!this.timestamp) {
      return 0;
    }
    const { collectionId } = options;
    try {
      const TRUE_VALUES = ["on", "true", "y", "yes", true];
      const userIsTest = TRUE_VALUES.includes(this.Atheneum.User.test);

      let query;

      if (!userIsTest) {
        // Combine the 'notEqual' and collectionId check into one filter
        query = this.db.contacts.filter((contact) => {
          const testIsNotTrue = contact.test !== true;
          // If collectionId is provided, both conditions must be true.
          // If not, only the first condition matters.
          return collectionId
            ? testIsNotTrue && contact.collection_id === collectionId
            : testIsNotTrue;
        });
      } else {
        if (collectionId) {
          query = this.db.contacts.where("collection_id").equals(collectionId);
        } else {
          query = this.db.contacts.toCollection();
        }
      }

      const count = await query.count();

      if (!collectionId) {
        this.totalContacts = count;
        localStorage.setItem(this.totalContactsKey, count.toString());
      }

      return count;
    } catch (err) {
      console.error("Error getting total contacts from Dexie.js:", err);
      return 0;
    }
  }

  saveToStorage() {
    console.log(
      "`saveToStorage` is deprecated. Contact persistence is managed directly by Dexie.js.",
    );
    this.caching = false;
  }

  async loadFromStorage(delay = false) {
    if (!this.db) {
      console.warn(
        "Dexie.js is not available. Cannot load contacts from storage.",
      );
      this.timestamp = undefined;
      this.totalContacts = 0;
      this.contactsLoaded = false;
      return false;
    }

    await this.initializeAndValidateCache();

    try {
      const currentUserId = this.Atheneum.User.id || "";
      const cachedUserId = localStorage.getItem(this.userIdKey);

      if (currentUserId && cachedUserId && currentUserId !== cachedUserId) {
        console.warn("User ID mismatch. Clearing contact cache.");
        await this.clearCache(); // This will safely run *before* we try to load.
      }
    } catch (err) {
      console.error("Error validating user ID during loadFromStorage:", err);
    }

    try {
      if (delay) {
        this.delayed = true;
        this.contactsLoaded = false;
        return true;
      }

      const cachedTimestamp = localStorage.getItem(this.timestampKey);
      this.timestamp = cachedTimestamp
        ? parseInt(cachedTimestamp, 10)
        : undefined;
      if (isNaN(this.timestamp)) this.timestamp = undefined;

      if (!this.timestamp || this.timestamp === 0) {
        this.totalContacts = 0;
      } else {
        this.totalContacts = await this.getTotalContacts();
      }

      this.contactsLoaded = false;
      this.caching = this.totalContacts === 0;
      return true;
    } catch (err) {
      console.error("Error loading contacts from Dexie.js:", err);
      this.totalContacts = 0;
      this.timestamp = undefined;
      this.contactsLoaded = false;
      return false;
    }
  }

  /**
   * Retrieves a sorted and paginated list of contacts.
   */
  async getContacts(options = {}) {
    if (!this.db) {
      console.warn("Dexie.js database not initialized.");
      return [];
    }

    const {
      collectionId,
      sortBy = "email",
      sortOrder = "asc",
      limit,
      offset = 0,
    } = options;

    try {
      const TRUE_VALUES = ["on", "true", "y", "yes", true];
      const userIsTest = TRUE_VALUES.includes(this.Atheneum.User.test);

      const sortFieldKey = sortBy.toLowerCase();
      // Prioritize normalized field for consistent sorting, then fall back.
      const sortDbField =
        this.normalizedFieldMap[sortFieldKey] ||
        this.fieldMap[sortFieldKey] ||
        `info_${sortFieldKey}`;

      let queryChain = this.db.contacts.orderBy(sortDbField);

      if (sortOrder.toLowerCase() === "desc") {
        queryChain = queryChain.reverse();
      }

      const filteredChain = queryChain.filter((contact) => {
        if (collectionId && contact.collection_id !== collectionId)
          return false;
        if (!userIsTest && contact.test === true) return false;
        return true;
      });

      const finalChain = filteredChain.offset(offset).limit(limit || Infinity);
      const flatContacts = await finalChain.toArray();

      return flatContacts.map(
        (contactData) =>
          new Atheneum.Contact(this.processContactForRetrieval(contactData)),
      );
    } catch (error) {
      console.error("Error in getContacts:", error);
      return [];
    }
  }

  async getByFieldValueAsync(
    fieldName,
    fieldValue,
    partial,
    limit,
    successCallbackOrCallbacks,
    failureCallback,
    offlineCallback,
    formFilterMode = false,
    formObj,
    $form,
  ) {
    const atheneum = this.Atheneum;
    const aThis = this;

    const callbacks = Atheneum.Callbacks.parse(
      successCallbackOrCallbacks,
      failureCallback,
      offlineCallback,
    );

    if (fieldValue === "" || fieldValue === null) {
      callbacks.success([]);
      return;
    }

    if (aThis.__preferLiveQueryToCache) {
      return atheneum.API.searchContacts(
        {
          fieldName: fieldName,
          query: fieldValue,
          partialMatch: partial,
          itemsPerPage: limit,
        },
        {
          success: async (searchResults) => {
            const contacts = [];
            if (searchResults && searchResults.contacts) {
              const processedContactsForDexie = [];
              for (const contactData of searchResults.contacts) {
                processedContactsForDexie.push(
                  this.processContactForStorage(contactData),
                );
              }
              await aThis.db.contacts.bulkPut(processedContactsForDexie);
              aThis.forceRecount = true;

              for (const processedContactData of processedContactsForDexie) {
                contacts.push(
                  new Atheneum.Contact(
                    this.processContactForRetrieval(processedContactData),
                  ),
                );
              }
            }
            if (searchResults.totalUnfilteredContacts) {
              aThis.totalContacts = searchResults.totalUnfilteredContacts;
            }
            callbacks.success(contacts);
          },
          failure: (reason, httpStatus, errorCode) => {
            callbacks.failure(
              reason,
              httpStatus,
              errorCode,
              "AtheneumContactsHelper.getByFieldValueAsync",
            );
          },
          offline: (reason, httpStatus, errorCode) => {
            callbacks.offline(
              reason,
              httpStatus,
              errorCode,
              "AtheneumContactsHelper.getByFieldValueAsync",
            );
          },
        },
      );
    } else {
      const options = {
        exact: partial == null ? null : !partial,
        consented: undefined,
        limit: limit,
        offset: 0,
      };

      if (formObj?.fields == undefined) formObj.fields = [];
      try {
        const contacts = await this.getByFieldValue(
          fieldName,
          fieldValue,
          options,
          formFilterMode,
          formObj,
          $form,
        );
        callbacks.success(contacts);
      } catch (error) {
        console.error("Error querying Dexie.js:", error);
        callbacks.failure(
          error.message,
          0,
          0,
          "AtheneumContactsHelper.getByFieldValueAsync",
        );
      }
    }
  }

  /**
   * Downloads contacts using a parallel "worker pool" strategy for maximum speed
   * while respecting mobile device memory and network constraints.
   * @param {object} callbacks - The parsed callback object (success, failure, progress).
   * @param {number} initiatedAt - The timestamp when the update process began.
   * @private
   */
  async downloadContactsInParallel(callbacks, initiatedAt) {
    const atheneum = this.Atheneum;
    const aThis = this;

    // Configuration for parallel downloads.
    const concurrency = atheneum.Theme.getIntegerProperty(
      "contactDownloadConcurrency",
      4,
    );
    const contactsPerPage = atheneum.Proxy.enabled
      ? this.__proxyContactPageSize
      : this.__contactPageSize;
    const delay = atheneum.Proxy.enabled
      ? this.__proxyDelayBetweenContactPageRequests
      : this.__delayBetweenContactPageRequests;

    let completedPages = 0;
    let totalPages = 1;
    let finalTimestamp = this.timestamp || 0;
    const isDeltaSync = aThis.timestamp > 0;

    let serverTotalContacts = 0;
    let modifiedContacts = 0;

    // Helper to download and process a single page.
    const downloadAndProcessPage = async (pageIndex) => {
      if (
        aThis.__abortAnyCacheAttemptStartedBefore &&
        aThis.__abortAnyCacheAttemptStartedBefore > initiatedAt
      ) {
        throw new Error(Atheneum.API.Problems.USER_ABORT);
      }

      const json = await new Promise((resolve, reject) => {
        atheneum.API.getContacts(
          contactsPerPage,
          pageIndex,
          aThis.timestamp || 0,
          {
            success: resolve,
            failure: (reason, httpStatus, errorCode) =>
              reject({
                reason,
                httpStatus,
                errorCode,
                source: "AtheneumContactsHelper.update",
              }),
            offline: (reason, httpStatus, errorCode) =>
              reject({
                reason,
                httpStatus,
                errorCode,
                source: "AtheneumContactsHelper.update",
              }),
          },
        );
      });

      if (json.contacts && json.contacts.length > 0) {
        await aThis.db.contacts.bulkPut(
          json.contacts.map((c) => this.processContactForStorage(c)),
        );
        aThis.forceRecount = true;
      }
      if (json.deletedContacts && json.deletedContacts.length > 0) {
        await aThis.db.contacts.bulkDelete(json.deletedContacts);
        aThis.forceRecount = true;
      }

      if (pageIndex === 0) {
        serverTotalContacts = json.totalContacts || 0;
        // This is the actual number of contacts we're downloading
        modifiedContacts =
          this.timestamp === undefined || this.timestamp === 0
            ? json.totalContacts || 0
            : json.modifiedContacts || json.contacts.length;
      }

      // Progress Reporting
      if (pageIndex > 0) {
        completedPages++; // We increment this as workers finish pages
        const now = new Date().getTime();
        const estimatedTimePerPage = (now - initiatedAt) / completedPages;
        const remainingPages = totalPages - completedPages;
        const percentage = (completedPages / totalPages) * 100;

        // --- NEW: Estimate synced contacts ---
        // This provides a meaningful number instead of 0.
        const estimatedSynced = Math.min(
          Math.floor((completedPages / totalPages) * modifiedContacts),
          modifiedContacts,
        );
        // --- END NEW ---

        callbacks.progress(
          {
            // --- USE NEW VALUES ---
            syncedContacts: estimatedSynced,
            totalContacts: serverTotalContacts,
            // --- END ---
            successes: completedPages,
            remaining: remainingPages,
            total: totalPages,
            done: remainingPages <= 0,
            percentage: percentage,
            eta: now + estimatedTimePerPage * remainingPages,
          },
          json,
        );
      }

      return json;
    };

    try {
      // Phase 1: Initial request to get total pages
      const firstPageJson = await downloadAndProcessPage(0);
      localStorage.setItem(aThis.userIdKey, atheneum.User.id || "");
      if (firstPageJson.timestamp) finalTimestamp = firstPageJson.timestamp;
      const serverTotalContacts = firstPageJson.totalContacts || 0;
      const total = firstPageJson.totalContacts || aThis.totalContacts;
      const modified =
        this.timestamp === undefined || this.timestamp === 0
          ? total
          : firstPageJson.modifiedContacts || firstPageJson.contacts.length;
      totalPages = Math.ceil(modified / contactsPerPage) || 1;

      const validationConfig = {
        callbacks,
        isDeltaSync,
        serverTotalContacts,
      };

      if (totalPages <= 1) {
        // Run the validation check.
        const resyncTriggered =
          await aThis._validateSyncAndTriggerResyncIfNeeded(validationConfig);
        // If the helper triggered a new update, stop the current process.
        if (resyncTriggered) return;
        // We're done if there's only one page
        if (aThis.forceRecount) await aThis.getTotalContacts();
        aThis.timestamp = finalTimestamp;
        localStorage.setItem(aThis.timestampKey, aThis.timestamp);
        return callbacks.success(firstPageJson);
      }

      // Phase 2: Worker pool for remaining pages
      const pageIndexesToDownload = Array.from(
        { length: totalPages - 1 },
        (_, i) => i + 1,
      );

      const worker = async () => {
        while (pageIndexesToDownload.length > 0) {
          const pageIndex = pageIndexesToDownload.shift();
          if (pageIndex !== undefined) {
            await downloadAndProcessPage(pageIndex);
            if (delay > 0)
              await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      };

      const workerPromises = Array.from(
        { length: Math.min(concurrency, pageIndexesToDownload.length) },
        () => worker(),
      );
      await Promise.all(workerPromises);

      // Run the same validation check after all workers are done.
      const resyncTriggered =
        await aThis._validateSyncAndTriggerResyncIfNeeded(validationConfig);
      if (resyncTriggered) return;

      // Finalization
      if (aThis.forceRecount) await aThis.getTotalContacts();
      aThis.timestamp = finalTimestamp;
      localStorage.setItem(aThis.timestampKey, aThis.timestamp);
      aThis.caching = false;
      callbacks.success(firstPageJson);
    } catch (error) {
      const problem = error.reason || error.message || "Unknown Error";
      const source = error.source || "AtheneumContactsHelper.update";
      const status =
        error.httpStatus ||
        (problem === Atheneum.API.Problems.USER_ABORT ? 200 : 0);
      callbacks.failure(problem, status, error.errorCode || 0, source);
    }
  }

  /**
   * Private helper that ONLY validates the final contact count.
   * If a mismatch is found on a delta sync, it triggers a full resync and returns true.
   * Otherwise, it returns false, allowing the original code path to continue.
   * @param {object} config - Configuration object.
   * @returns {boolean} - True if a resync was triggered, otherwise false.
   * @private
   */
  async _validateSyncAndTriggerResyncIfNeeded(config) {
    const { callbacks, isDeltaSync, serverTotalContacts } = config;

    // Get the final local count after all updates are processed.
    const finalLocalCount = await this.getTotalContacts();

    if (isDeltaSync && finalLocalCount !== serverTotalContacts) {
      console.warn(
        `Post-sync validation failed! Local: ${finalLocalCount}, Server: ${serverTotalContacts}. Triggering a full resync.`,
      );
      await this.clearCache();
      this.update(callbacks.success, callbacks.failure, callbacks.offline);
      return true; // Signal that a resync has been started.
    }

    return false; // Signal that everything is OK to proceed.
  }

  async downloadContacts(config) {
    const atheneum = this.Atheneum;
    const aThis = this;

    if (
      atheneum.Theme.getBooleanProperty("downloadContactsInParallel", false)
    ) {
      // Use the new, fast parallel method
      this.downloadContactsInParallel(config?.callbacks, config?.initiatedAt);
      return;
    }

    if (
      aThis.__abortAnyCacheAttemptStartedBefore &&
      aThis.__abortAnyCacheAttemptStartedBefore > config.initiatedAt
    ) {
      return config.callbacks.failure(
        Atheneum.API.Problems.USER_ABORT,
        200,
        0,
        "AtheneumContactsHelper.update",
      );
    }

    if (!config.activePromises) config.activePromises = [];
    if (!config.maximumConcurrentInserts)
      config.maximumConcurrentInserts = this.Atheneum.Theme.getIntegerProperty(
        "maximumConcurrentContactInserts",
        1,
      );

    if (config.retryCount === undefined) config.retryCount = 0;

    let requestCompleted = false;

    const downloadSuccessCallback = async (json) => {
      if (
        aThis.__abortAnyCacheAttemptStartedBefore &&
        aThis.__abortAnyCacheAttemptStartedBefore > config.initiatedAt
      ) {
        return config.callbacks.failure(
          Atheneum.API.Problems.USER_ABORT,
          200,
          0,
          "AtheneumContactsHelper.update",
        );
      }

      config.retryCount = 0;

      if (config.pageIndex === 0) {
        localStorage.setItem(aThis.userIdKey, atheneum.User.id || "");
        config.serverTotalContacts = json.totalContacts;
      }

      if (config.timestampAfter === undefined && json.timestamp) {
        config.timestampAfter = json.timestamp;
      }

      const reportSavingProgress = () => {
        const savePercentage =
          (config.completedInserts.count / config.totalInserts.count) * 100;

        config.callbacks.progress({
          stage: "saving",
          percentage: savePercentage,
          current: config.completedInserts.count,
          total: config.totalInserts.count,
        });
      };

      const trackDatabaseOperation = (dbPromise) => {
        const trackerPromise = dbPromise.finally(() => {
          config.completedInserts.count++;
          reportSavingProgress();
        });
        config.activePromises.push(trackerPromise);

        trackerPromise.finally(() => {
          const index = config.activePromises.indexOf(trackerPromise);
          if (index > -1) {
            config.activePromises.splice(index, 1);
          }
        });
      };

      if (json.contacts && json.contacts.length > 0) {
        let contactsToAddOrUpdate = json.contacts.map((contactData) =>
          this.processContactForStorage(contactData),
        );

        json.contacts = null; // Free memory
        const insertPromise = aThis.db.contacts.bulkPut(contactsToAddOrUpdate);
        trackDatabaseOperation(insertPromise); // Use the tracker
        aThis.forceRecount = true;

        contactsToAddOrUpdate = null; // Free memory
      }

      if (json.deletedContacts && json.deletedContacts.length > 0) {
        const deletePromise = aThis.db.contacts.bulkDelete(
          json.deletedContacts,
        );
        trackDatabaseOperation(deletePromise); // Use the tracker
        aThis.forceRecount = true;
        json.deletedContacts = null; // Free memory
      }

      while (config.activePromises.length >= config.maximumConcurrentInserts) {
        await Promise.race(config.activePromises);
      }

      // if (aThis.forceRecount) {
      //   await aThis.getTotalContacts();
      //   aThis.forceRecount = false;
      // }

      const totalContacts =
        config.serverTotalContacts || json.totalContacts || aThis.totalContacts;
      const modifiedContacts =
        config.timestamp === undefined || config.timestamp === 0
          ? totalContacts
          : json.modifiedContacts || json.contacts.length;
      aThis.caching = !(
        (config.pageIndex || 0) === 0 &&
        (config.timestamp === undefined || config.timestamp === 0) &&
        totalContacts <= aThis.totalContacts
      );

      const totalPages = Math.ceil(modifiedContacts / config.contactsPerPage);
      if (config.pageIndex === 0) {
        config.totalInserts.count = totalPages;
      }

      const remainingPagesToDownload = Math.max(
        0,
        (totalPages || 1) - config.pageIndex - 1,
      );

      const estimatedProcessed =
        (config.pageIndex + 1) * config.contactsPerPage;
      const estimatedSyncedContacts = Math.min(
        estimatedProcessed,
        modifiedContacts,
      );

      const now = new Date().getTime();
      let estimatedTimePerPage =
        (now - config.initiatedAt) / ((config.pageIndex || 0) + 1);
      if (estimatedTimePerPage === 0) {
        estimatedTimePerPage = config.delay || 50;
      }
      const estimatedCompletionDate =
        now + estimatedTimePerPage * remainingPagesToDownload;

      if (typeof config.callbacks.progress === "function") {
        const downloadPercentage =
          ((config.pageIndex + 1) / (totalPages || 1)) * 100;
        const totalPercentage = downloadPercentage;

        config.callbacks.progress(
          {
            stage: "downloading",
            failured: 0,
            syncedContacts: estimatedSyncedContacts,
            totalContacts: totalContacts,
            successes: config.pageIndex + 1,
            remaining: remainingPagesToDownload,
            total: totalPages || 1,
            done: remainingPagesToDownload === 0,
            percentage: totalPercentage,
            eta: estimatedCompletionDate,
          },
          json,
        );
      }

      if (remainingPagesToDownload === 0) {
        const progressInterval = setInterval(reportSavingProgress, 250);

        try {
          await Promise.allSettled(config.activePromises);
        } catch (error) {
          console.error("Error waiting for background inserts:", error);
        }

        clearInterval(progressInterval);
        if (config.completedInserts.count < config.totalInserts.count) {
          config.completedInserts.count = config.totalInserts.count;
        }
        reportSavingProgress();

        aThis.timestamp = config.timestampAfter;
        if (aThis.timestamp) {
          localStorage.setItem(aThis.timestampKey, aThis.timestamp);
        }

        const finalLocalCount = await aThis.getTotalContacts();
        const serverTotalCount = config.serverTotalContacts || finalLocalCount; // Fallback

        if (config.timestamp > 0 && finalLocalCount !== serverTotalCount) {
          console.warn(
            `Sync complete, but count mismatch detected! Local: ${finalLocalCount}, Server: ${serverTotalCount}. Triggering a full resync.`,
          );

          // Clear cache and trigger a new, full update (timestamp will be 0)
          await aThis.clearCache();
          aThis.update(
            config.callbacks.success,
            config.callbacks.failure,
            config.callbacks.offline,
          );

          // We exit here because the new update process has taken over.
          return;
        }

        config.callbacks.success(json);
      } else {
        const configForNextDownloadCall = {
          ...config,
          pageIndex: config.pageIndex + 1,
        };

        if (config.delay) {
          window.setTimeout(
            () => aThis.downloadContacts(configForNextDownloadCall),
            config.delay,
          );
        } else {
          window.setTimeout(
            () => aThis.downloadContacts(configForNextDownloadCall),
            0,
          );
        }
      }
    };

    const NETWORK_TIMEOUT_MS = 30000;

    const timeoutTimer = setTimeout(() => {
      // ⚡ LOCKOUT: If this timer fires, we lock this specific attempt forever.
      if (requestCompleted) return;
      requestCompleted = true;

      console.warn(
        `Contact Sync: Page ${config.pageIndex} timed out (Attempt ${
          config.retryCount + 1
        }).`,
      );

      if (config.retryCount < 3) {
        config.retryCount++;
        setTimeout(() => {
          console.log(`Retrying page ${config.pageIndex}...`);
          aThis.downloadContacts(config); // Start NEW attempt
        }, 2000);
      } else {
        config.callbacks.failure(
          "Network Timeout: Connection hanging",
          408,
          0,
          "AtheneumContactsHelper.update",
        );
      }
    }, NETWORK_TIMEOUT_MS);

    this.Atheneum.API.getContacts(
      config.contactsPerPage,
      config.pageIndex,
      this.timestamp || 0,
      {
        success: (json) => {
          if (requestCompleted) return;

          clearTimeout(timeoutTimer);
          requestCompleted = true;

          downloadSuccessCallback(json);
        },
        failure: (reason, httpStatus, errorCode) => {
          if (requestCompleted) return;

          clearTimeout(timeoutTimer);
          requestCompleted = true;

          config.callbacks.failure(
            reason,
            httpStatus,
            errorCode,
            "AtheneumContactsHelper.update",
          );
        },
        offline: (reason, httpStatus, errorCode) => {
          if (requestCompleted) return;

          clearTimeout(timeoutTimer);
          requestCompleted = true;

          config.callbacks.offline(
            reason,
            httpStatus,
            errorCode,
            "AtheneumContactsHelper.update",
          );
        },
      },
    );
  }

  /**
   * Orchestrates the download of contact snapshots with Retry & Resume capability.
   */
  async downloadContactsFromSnapshots(callbacks, initiatedAt) {
    const atheneum = this.Atheneum;
    const apiKey = atheneum.User.apikey;
    const MAX_RETRIES = 3;
    const NETWORK_TIMEOUT_MS = 30000;

    if (!apiKey) {
      callbacks.failure(
        "Atheneum API key is missing.",
        401,
        0,
        "AtheneumContactsHelper.downloadContactsFromSnapshots",
      );
      return;
    }

    try {
      const timestampParam = this.timestamp || 0;
      const listUrl = `${atheneum.API.BASE_URL}/contacts/snapshots?timestamp=${timestampParam}`;

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), NETWORK_TIMEOUT_MS);

      let listResponse;
      try {
          listResponse = await fetch(listUrl, {
            method: "GET",
            headers: { Authorization: `Bearer ${apiKey}` },
            signal: controller.signal
          });
      } catch (error) {
          if (error.name === 'AbortError') {
              throw new Error(`Network timeout fetching snapshot list (${NETWORK_TIMEOUT_MS}ms)`);
          }
          throw error;
      } finally {
          clearTimeout(timeoutId);
      }

      if (!listResponse.ok) {
        throw new Error(
          `Failed to fetch snapshot list: ${listResponse.status}`,
        );
      }

      const listData = await listResponse.json();
      const snapshots = listData.snapshots || [];
      const globalTotalContacts = listData.totalContacts || 0;
      const finalTimestamp = listData.timestamp;

      // If the server says there are no new snapshots for this timestamp,
      // we must fall back to legacy to catch whatever small changes exist.
      if (snapshots.length === 0) {
        console.log(
          "No snapshots returned by server. Falling back to legacy sync.",
        );
        this.update(
          callbacks.success,
          callbacks.failure,
          callbacks.offline,
          true,
        );
        return;
      }

      console.log(`Downloading ${snapshots.length} snapshots...`);
      let processedGlobalCount = 0;

      for (let i = 0; i < snapshots.length; i++) {
        const snapshot = snapshots[i];
        const userIsTest = ["on", "true", "y", "yes", true].includes(
          atheneum.User.test,
        );
        if (snapshot.test && !userIsTest) continue;

        let attempts = 0;
        let success = false;
        let linesSavedForThisSnapshot = 0;

        while (attempts < MAX_RETRIES && !success) {
          try {
            if (
              this.__abortAnyCacheAttemptStartedBefore &&
              this.__abortAnyCacheAttemptStartedBefore > initiatedAt
            ) {
              throw new Error(Atheneum.API.Problems.USER_ABORT);
            }

            if (attempts > 0) {
              console.log(
                `Retrying snapshot ${snapshot.id} (Attempt ${attempts + 1}). Resuming after ${linesSavedForThisSnapshot} lines.`,
              );
            }

            await this.processSnapshotStream(
              snapshot.id,
              apiKey,
              initiatedAt,
              linesSavedForThisSnapshot,
              (countAdded) => {
                linesSavedForThisSnapshot += countAdded;
                processedGlobalCount += countAdded;

                let eta = 0;
                const now = Date.now();
                const elapsed = now - initiatedAt;

                if (processedGlobalCount > 0 && globalTotalContacts > 0) {
                  const rate = processedGlobalCount / elapsed;
                  const remainingContacts =
                    globalTotalContacts - processedGlobalCount;
                  if (rate > 0) {
                    eta = now + remainingContacts / rate;
                  }
                }

                const progressData = {
                  stage: "downloading_snapshot",
                  syncedContacts: processedGlobalCount,
                  totalContacts: globalTotalContacts,
                  successes: i,
                  total: snapshots.length,
                  percentage:
                    (processedGlobalCount / (globalTotalContacts || 1)) * 100,
                  eta: eta,
                };

                if (callbacks.progress) {
                  callbacks.progress(progressData);
                } else if (
                  window.SalesToolkitApplication &&
                  window.SalesToolkitApplication.Events &&
                  window.SalesToolkitApplication.Events
                    .CONTACT_SNAPSHOT_PROGRESS
                ) {
                  document.dispatchEvent(
                    new CustomEvent(
                      window.SalesToolkitApplication.Events
                        .CONTACT_SNAPSHOT_PROGRESS,
                      {
                        detail: progressData,
                      },
                    ),
                  );
                }
              },
            );
            success = true;
          } catch (err) {
            if (err.message === Atheneum.API.Problems.USER_ABORT) throw err;
            if (err.message && err.message.includes("Network timeout")) throw err;
            attempts++;
            console.warn(
              `Snapshot ${snapshot.id} failed (Attempt ${attempts}/${MAX_RETRIES}).`,
              err,
            );
            if (attempts >= MAX_RETRIES)
              throw new Error(
                `Max retries reached for snapshot ${snapshot.id}`,
              );
            await new Promise((resolve) => setTimeout(resolve, 2000));
          }
        }
      }

      this.timestamp = finalTimestamp;
      localStorage.setItem(this.timestampKey, this.timestamp);
      this.forceRecount = true;
      const finalLocalCount = await this.getTotalContacts();

      // If we have a mismatch, something went wrong. Clear and restart via Legacy.
      if (finalLocalCount !== globalTotalContacts) {
        console.warn(
          `Snapshot count mismatch! Local: ${finalLocalCount}, Expected: ${globalTotalContacts}. Triggering full legacy resync.`,
        );
        await this.clearCache();
        // Fallback to legacy (forceLegacy=true)
        this.update(
          callbacks.success,
          callbacks.failure,
          callbacks.offline,
          true,
        );
        return;
      }

      console.log("Snapshot download complete.");

      if (callbacks.success) {
        callbacks.success();
      }

      if (window.SalesToolkitApplication && window.SalesToolkitApplication.Events && window.SalesToolkitApplication.Events.CONTACT_SNAPSHOT_FINISHED) {
          document.dispatchEvent(new CustomEvent(window.SalesToolkitApplication.Events.CONTACT_SNAPSHOT_FINISHED));
      }
    } catch (err) {
      if (err.message === Atheneum.API.Problems.USER_ABORT) {
        callbacks.failure(
          err.message,
          200,
          0,
          "AtheneumContactsHelper.downloadContactsFromSnapshots",
        );
        return;
      }

      if (err.message && err.message.includes("Network timeout")) {
          console.warn("Snapshot download timed out. Aborting sync.", err);
          
          const userMessage = atheneum.Translation.getOrGetThemeKey("contactSnapshotTimeoutMessage", "The contact snapshot download timed out due to network issues. Please check your connection and try again.");

          if (window.SalesToolkitApplication && window.SalesToolkitApplication.Events && window.SalesToolkitApplication.Events.CONTACT_SNAPSHOT_FAILED) {
              document.dispatchEvent(new CustomEvent(window.SalesToolkitApplication.Events.CONTACT_SNAPSHOT_FAILED, {
                  detail: { message: userMessage }
              }));
          }

          // Call failure callback and RETURN (Do not fall back to legacy)
          callbacks.failure(userMessage, 408, 0, "AtheneumContactsHelper.downloadContactsFromSnapshots");
          return;
      }


      console.warn("Snapshot download failed.", err);
      // Fallback to legacy (forceLegacy=true)
      this.update(
        callbacks.success,
        callbacks.failure,
        callbacks.offline,
        true,
      );
    }
  }

  /**
   * Streams a single NDJSON snapshot file, parses lines, and batches inserts.
   * Accepts 'skipLines' to support resumable downloads.
   */
  async processSnapshotStream(
    snapshotId,
    apiKey,
    initiatedAt,
    skipLines,
    onProgress,
  ) {
    const atheneum = this.Atheneum;
    const url = `${atheneum.API.BASE_URL}/contacts/snapshots/${snapshotId}`;
    const NETWORK_TIMEOUT_MS = 30000; // 30s connection timeout

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), NETWORK_TIMEOUT_MS);

   let response;
    try {
        response = await fetch(url, {
          method: "GET",
          headers: { Authorization: `Bearer ${apiKey}` },
          signal: controller.signal
        });
    } catch (error) {
        if (error.name === 'AbortError') {
            throw new Error(`Network timeout connecting to snapshot stream (${NETWORK_TIMEOUT_MS}ms)`);
        }
        throw error;
    } finally {
        clearTimeout(timeoutId);
    }

    if (!response.ok)
      throw new Error(`Failed to stream snapshot ${snapshotId}`);

    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");

    const BATCH_SIZE = this.Atheneum.Theme.getIntegerProperty(
      "snapshotContactBatchSize",
      1000,
    );

    let batch = [];
    let pendingStr = "";
    let currentLineIndex = 0;

    while (true) {
      if (
        this.__abortAnyCacheAttemptStartedBefore &&
        this.__abortAnyCacheAttemptStartedBefore > initiatedAt
      ) {
        await reader.cancel();
        throw new Error(Atheneum.API.Problems.USER_ABORT);
      }

     const readTimeoutId = setTimeout(() => controller.abort(), NETWORK_TIMEOUT_MS);
      
      let done, value;
      try {
          ({ done, value } = await reader.read());
      } catch (error) {
          if (error.name === 'AbortError') {
              throw new Error(`Network timeout reading snapshot stream (stalled for ${NETWORK_TIMEOUT_MS}ms)`);
          }
          throw error;
      } finally {
          clearTimeout(readTimeoutId);
      }

      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const currentStr = pendingStr + chunk;
      const lines = currentStr.split("\n");

      pendingStr = lines.pop();

      for (const line of lines) {
        if (!line.trim()) continue;

        if (currentLineIndex < skipLines) {
          currentLineIndex++;
          continue;
        }

        try {
          const contactData = JSON.parse(line);
          const processed = this.processContactForStorage(contactData);
          batch.push(processed);
          currentLineIndex++;
        } catch (e) {
          console.warn("Failed to parse contact line in snapshot", e);
        }

        if (batch.length >= BATCH_SIZE) {
          await this.db.contacts.bulkPut(batch);
          onProgress(batch.length);
          batch = [];
        }
      }
    }

    if (pendingStr.trim()) {
      if (currentLineIndex >= skipLines) {
        try {
          const contactData = JSON.parse(pendingStr);
          batch.push(this.processContactForStorage(contactData));
        } catch (e) {
          console.warn("Failed to parse final line", e);
        }
      }
    }

    if (batch.length > 0) {
      await this.db.contacts.bulkPut(batch);
      onProgress(batch.length);
    }
  }

  _performLegacyUpdate(callbacks, initiatedAt) {
    const atheneum = this.Atheneum;

    if (
      atheneum.Theme.getBooleanProperty("downloadContactsInParallel", false)
    ) {
      this.downloadContactsInParallel(callbacks, initiatedAt);
    } else {
      const downloadContactsConfig = {
        initiatedAt: initiatedAt,
        timestamp: this.timestamp || 0,
        contactsPerPage: atheneum.Proxy.enabled
          ? this.__proxyContactPageSize
          : this.__contactPageSize,
        pageIndex: 0,
        callbacks: callbacks,
        delay: atheneum.Proxy.enabled
          ? this.__proxyDelayBetweenContactPageRequests
          : this.__delayBetweenContactPageRequests,
        completedInserts: { count: 0 },
        totalInserts: { count: 0 },
      };
      this.downloadContacts(downloadContactsConfig);
    }
  }

  update(
    successCallbackOrCallbacks,
    failureCallback,
    offlineCallback,
    forceLegacy = false,
  ) {
    const atheneum = this.Atheneum;

    // Parse callbacks
    const callbacks = Atheneum.Callbacks.parse(
      successCallbackOrCallbacks,
      failureCallback,
      offlineCallback,
    );

    if (atheneum.Theme.getBooleanProperty("disableContacts", false)) {
      try {
        callbacks.failure();
        return;
      } catch (error) {
        console.error(error);
      }
    }

    if (!callbacks.progress) {
      callbacks.progress = (progress, json) => {};
    }

    const initiatedAt = new Date().getTime();
    this.__abortAnyCacheAttemptStartedBefore = initiatedAt;

    this.__delayBetweenContactPageRequests = atheneum.Theme.getIntegerProperty(
      "delayBetweenContactPageRequests",
      0,
    );

    const useSnapshots = atheneum.Theme.getBooleanProperty(
      "useContactSnapshotApis",
      false,
    );
    const isColdStart = !this.timestamp || this.timestamp === 0;

    // --- PATH 1: Cold Start (Fresh Install) ---
    // If enabled, not forced to legacy, and no data exists -> Go straight to snapshots.
    if (useSnapshots && isColdStart && !forceLegacy) {
      this.downloadContactsFromSnapshots(callbacks, initiatedAt);
      return;
    }

    // --- PATH 2: Warm Start (Large Update Check) ---
    // If enabled, we have data, and not forced to legacy -> Check if the update is massive.
    if (useSnapshots && !isColdStart && !forceLegacy) {
      this._attemptLargeDeltaSnapshotSync(callbacks, initiatedAt)
        .then((processedViaSnapshots) => {
          // If checking returned false (small update or error), use standard legacy sync.
          if (!processedViaSnapshots) {
            this._performLegacyUpdate(callbacks, initiatedAt);
          }
        })
        .catch((err) => {
          console.warn(
            "Error during large delta check, defaulting to legacy:",
            err,
          );
          this._performLegacyUpdate(callbacks, initiatedAt);
        });
      return;
    }

    // --- PATH 3: Legacy Fallback ---
    this._performLegacyUpdate(callbacks, initiatedAt);
  }

  /**
   * Checks metadata first. If 'modifiedContacts' > Threshold, handles deletions
   * and triggers the Snapshot flow. Returns true if Snapshots started, false if Legacy needed.
   */
  async _attemptLargeDeltaSnapshotSync(callbacks, initiatedAt) {
    const atheneum = this.Atheneum;
    const apiKey = atheneum.User.apikey;

    try {
      // 1. Fetch Metadata (itemsPerPage=1 to get counts/deletedContacts efficiently)
      const url = `${atheneum.API.BASE_URL}/contacts?timestamp=${this.timestamp}&itemsPerPage=1&pageIndex=0`;

      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${apiKey}` },
      });

      if (!response.ok) return false;

      const json = await response.json();

      if (json.errorCode !== 0) return false;

      // 2. Handle Deletions Immediately
      // We must clean up deleted contacts before applying snapshots (which are additive)
      if (json.deletedContacts && json.deletedContacts.length > 0) {
        console.log(
          `Deleting ${json.deletedContacts.length} contacts before snapshot.`,
        );
        await this.db.contacts.bulkDelete(json.deletedContacts);
        this.forceRecount = true;
      }

      // 3. Check Threshold
      const threshold = this.Atheneum.Theme.getIntegerProperty(
        "contactsPerPage",
        10000,
      );

      // if there are no modifications, no need to proceed
      if (json.modifiedContacts === 0) {
        console.log("No modified contacts detected. Sync complete.");

        // Update Timestamp to the one returned by server
        if (json.timestamp) {
          this.timestamp = json.timestamp;
          localStorage.setItem(this.timestampKey, this.timestamp);
        }

        // Trigger success immediately
        if (callbacks.success) callbacks.success();

        return true; // Handled (prevents legacy update)
      }
      // If modifications are small, return false to proceed with Legacy Sync
      if (json.modifiedContacts < threshold) {
        return false;
      }

      // 4. LARGE UPDATE DETECTED
      console.log(
        `Large contact update detected (${json.modifiedContacts} changes). Switching to Snapshot flow.`,
      );

      // 5. Trigger Snapshot Download
      // When this finishes (success or failure), it handles the next steps itself.
      this.downloadContactsFromSnapshots(callbacks, initiatedAt);

      return true; // Signal that we have handled the routing
    } catch (error) {
      console.warn("Failed to check for large delta.", error);
      return false; // Fallback to legacy
    }
  }

  /**
   * Asynchronously and efficiently retrieves contacts. This method dynamically chooses the search strategy.
   * If 'allowSearchTokensForDB' is true, it performs a high-performance "contains" search using a multi-entry index.
   * Otherwise, it falls back to a prioritized, indexed "startsWith" search.
   * @param {object} criteria - An object containing search criteria (e.g., {firstName: 'jo', country: 'usa'}).
   * @returns {Promise<Array<Atheneum.Contact>>} A promise that resolves to an array of matching Atheneum.Contact instances.
   */
  async getContactsByMultipleCriteria(criteria) {
    if (!this.db) {
      console.warn(
        "Dexie.js database not initialized. Cannot retrieve contacts.",
      );
      return [];
    }

    if (this.allowSearchTokens) {
      // Strategy: Token-based "contains" or "fuzzy" search.
      const searchTerms = Object.values(criteria).filter(
        (v) => typeof v === "string" && v.length > 0,
      );
      if (searchTerms.length === 0) return [];

      try {
        const normalizedTokens = searchTerms.map((term) =>
          this._normalizeStringForSearch(term),
        );

        const matchingIdSets = await Promise.all(
          normalizedTokens.map((token) => {
            if (this.allowFuzzySearchTerms) {
              const searchCandidates = this._generateFuzzyCandidates(token);
              return this.db.contacts
                .where("_search_tokens")
                .anyOf(searchCandidates)
                .primaryKeys()
                .then((ids) => new Set(ids));
            } else {
              return this.db.contacts
                .where("_search_tokens")
                .startsWith(token)
                .primaryKeys()
                .then((ids) => new Set(ids));
            }
          }),
        );

        if (matchingIdSets.some((s) => s.size === 0)) return [];

        const finalIds = matchingIdSets.reduce(
          (a, b) => new Set([...a].filter((x) => b.has(x))),
        );
        if (finalIds.size === 0) return [];

        const flatContacts = await this.db.contacts.bulkGet(
          Array.from(finalIds),
        );
        return flatContacts
          .filter(Boolean)
          .map((c) => new Atheneum.Contact(this.processContactForRetrieval(c)));
      } catch (error) {
        console.error("Error executing multi-criteria fuzzy search:", error);
        return [];
      }
    } else {
      // Strategy: Prioritized indexed "startsWith" search.
      if (Object.keys(criteria).length === 0) return [];

      const isSearchableString = (val) => typeof val === "string" && val !== "";

      const searchStrategies = [
        {
          name: "email",
          requiredFields: ["email"],
          getQuery: (c) =>
            this.db.contacts
              .where("email_normalized")
              .equals(this._normalizeStringForSearch(c.email)),
        },
        {
          name: "emails",
          requiredFields: ["emails"],
          getQuery: (c) =>
            this.db.contacts
              .where("email_normalized")
              .anyOf(
                c.emails.map((e) =>
                  this._normalizeStringForSearch(e.address || e),
                ),
              ),
        },
        {
          name: "fullNameAndCountry",
          requiredFields: ["lastName", "firstName", "country"],
          getQuery: (c) => {
            const values = [
              this._normalizeStringForSearch(c.lastName),
              this._normalizeStringForSearch(c.firstName),
              this._normalizeStringForSearch(c.country),
            ];
            return this.db.contacts
              .where(
                "[info_lastName_normalized+info_firstName_normalized+info_country_normalized]",
              )
              .between(values, [
                ...values.slice(0, -1),
                values[values.length - 1] + "\uffff",
              ]);
          },
        },
        {
          name: "fullName",
          requiredFields: ["lastName", "firstName"],
          getQuery: (c) => {
            const values = [
              this._normalizeStringForSearch(c.lastName),
              this._normalizeStringForSearch(c.firstName),
            ];
            return this.db.contacts
              .where("[info_lastName_normalized+info_firstName_normalized]")
              .between(values, [
                ...values.slice(0, -1),
                values[values.length - 1] + "\uffff",
              ]);
          },
        },
        {
          name: "firstNameAndCountry",
          requiredFields: ["firstName", "country"],
          getQuery: (c) => {
            const values = [
              this._normalizeStringForSearch(c.firstName),
              this._normalizeStringForSearch(c.country),
            ];
            return this.db.contacts
              .where("[info_firstName_normalized+info_country_normalized]")
              .between(values, [
                ...values.slice(0, -1),
                values[values.length - 1] + "\uffff",
              ]);
          },
        },
        {
          name: "lastNameAndCountry",
          requiredFields: ["lastName", "country"],
          getQuery: (c) => {
            const values = [
              this._normalizeStringForSearch(c.lastName),
              this._normalizeStringForSearch(c.country),
            ];
            return this.db.contacts
              .where("[info_lastName_normalized+info_country_normalized]")
              .between(values, [
                ...values.slice(0, -1),
                values[values.length - 1] + "\uffff",
              ]);
          },
        },
      ];

      this.dynamicCompoundIndexes.forEach((dynamicIndex) => {
        searchStrategies.push({
          name: dynamicIndex.name,
          requiredFields: dynamicIndex.fields,
          getQuery: (c) => {
            const queryValues = dynamicIndex.fields.map((field) =>
              this._normalizeStringForSearch(c[field]),
            );
            return this.db.contacts
              .where(dynamicIndex.dexieName)
              .between(queryValues, [
                ...queryValues.slice(0, -1),
                queryValues[queryValues.length - 1] + "\uffff",
              ]);
          },
        });
      });

      const singleFieldPriority = ["lastName", "firstName", "country"];
      singleFieldPriority.forEach((field) => {
        searchStrategies.push({
          name: field,
          requiredFields: [field],
          getQuery: (c) => {
            const normalizedFieldName =
              this.normalizedFieldMap[field.toLowerCase()] ||
              `info_${field.toLowerCase()}_normalized`;
            return this.db.contacts
              .where(normalizedFieldName)
              .startsWith(this._normalizeStringForSearch(c[field]));
          },
        });
      });

      for (const strategy of searchStrategies) {
        const canRunStrategy = strategy.requiredFields.every((field) =>
          isSearchableString(criteria[field]),
        );

        if (canRunStrategy) {
          try {
            const initialQuery = strategy.getQuery(criteria);
            const remainingCriteria = { ...criteria };
            strategy.requiredFields.forEach(
              (field) => delete remainingCriteria[field],
            );

            const flatContacts = await initialQuery
              .filter((contact) =>
                this._matchesRemainingCriteria(contact, remainingCriteria),
              )
              .toArray();

            if (flatContacts.length > 0) {
              return flatContacts.map(
                (contactData) =>
                  new Atheneum.Contact(
                    this.processContactForRetrieval(contactData),
                  ),
              );
            }
          } catch (error) {
            console.warn(
              `Search strategy '${strategy.name}' failed. Trying next strategy.`,
              error,
            );
          }
        }
      }

      return []; // Return empty if no strategy found any results
    }
  }

  /**
   * Private helper to check if a contact matches a set of criteria.
   * This is used as the secondary filter for the "startsWith" search fallback.
   */
  _matchesRemainingCriteria(contact, criteria) {
    const TRUE_VALUES = ["on", "true", "y", "yes", true];
    if (
      !TRUE_VALUES.includes(this.Atheneum.User.test) &&
      contact.test === true
    ) {
      return false;
    }

    for (const key in criteria) {
      const value = criteria[key];
      if (!value) continue;

      const normalizedValue = this._normalizeStringForSearch(String(value));

      // This logic performs a "contains" check for remaining criteria.
      if (
        key === "firstName" &&
        (!contact.info_firstName_normalized ||
          !contact.info_firstName_normalized.includes(normalizedValue))
      )
        return false;
      if (
        key === "lastName" &&
        (!contact.info_lastName_normalized ||
          !contact.info_lastName_normalized.includes(normalizedValue))
      )
        return false;
      if (
        key === "country" &&
        (!contact.info_country_normalized ||
          !contact.info_country_normalized.includes(normalizedValue))
      )
        return false;
      if (key === "emails" || key === "email") {
        const emailsToSearch = Array.isArray(value)
          ? value.map((e) => this._normalizeStringForSearch(e.address || e))
          : [normalizedValue];
        if (
          !contact.email_normalized ||
          !emailsToSearch.includes(contact.email_normalized)
        )
          return false;
      }

      if (key === "company") {
        const companyFieldName = this.Atheneum.Theme.getStringProperty(
          "companyContactFieldName",
          "company",
        );
        const companyNormalizedDbField =
          this.normalizedFieldMap[companyFieldName] ||
          `info_${companyFieldName}_normalized`;
        if (
          !contact[companyNormalizedDbField] ||
          !contact[companyNormalizedDbField].includes(
            this._normalizeStringForSearch(value),
          )
        ) {
          return false;
        }
      } else if (key === "phones" && Array.isArray(value) && value.length > 0) {
        const cleanPhones = value
          .map((p) => (p.number || p).replace(/[^\d]/g, ""))
          .filter(Boolean);
        const contactPhoneFields = [
          "info_phone",
          "info_phoneNumber",
          "info_phoneNo",
        ];
        const hasMatch = cleanPhones.some((phone) =>
          contactPhoneFields.some(
            (field) =>
              contact[field] &&
              String(contact[field]).replace(/[^\d]/g, "").startsWith(phone),
          ),
        );
        if (!hasMatch) return false;
      }
    }

    return true;
  }

  /**
   * Executes a SQL-like query against the contacts database.
   */
  async query(
    query,
    successCallbackOrCallbacks,
    failureCallback,
    offlineCallback,
  ) {
    const aThis = this;
    const callbacks = Atheneum.Callbacks.parse(
      successCallbackOrCallbacks,
      failureCallback,
      offlineCallback,
    );
    if (!this.db) {
      callbacks.failure(
        "Dexie.js database not initialized.",
        0,
        0,
        "AtheneumContactsHelper.query",
      );
      return;
    }

    let buffer = "";
    let bufferType = undefined;
    let stringType = undefined;
    const tokens = [];

    // Optimized token parsing loop
    for (let c = 0; c < query.length; c++) {
      const char = query[c];

      if (
        (bufferType === "object" || bufferType === "symbol") &&
        (char === " " || char === "\n" || char === "\t")
      ) {
        if (bufferType === "object") {
          const lowerBuffer = buffer.toLowerCase();
          if (["true", "false"].includes(lowerBuffer)) {
            bufferType = "boolean";
            buffer = lowerBuffer;
          } else if (
            [
              "select",
              "from",
              "like",
              "where",
              "limit",
              "offset",
              "and",
              "or",
            ].includes(lowerBuffer)
          ) {
            bufferType = "keyword";
            buffer = lowerBuffer;
          }
        }
        tokens.push({ type: bufferType, value: buffer });
        bufferType = "whitespace";
        buffer = "";
      }
      if (
        (bufferType === undefined || bufferType === "whitespace") &&
        (char === " " || char === "\n" || char === "\t")
      ) {
        bufferType = "whitespace";
        buffer += char;
        continue;
      }
      if (bufferType === "whitespace") {
        tokens.push({ type: bufferType, value: buffer });
        bufferType = undefined;
        buffer = "";
      }
      if (bufferType === undefined && (char === '"' || char === "'")) {
        stringType = char;
        bufferType = "string";
        continue;
      }
      if (bufferType === "string" && char === stringType) {
        tokens.push({ type: "string", value: buffer });
        bufferType = undefined;
        stringType = undefined;
        buffer = "";
        continue;
      }
      if (bufferType !== "string" && ["=", "!", "&", "|", "*"].includes(char)) {
        if (buffer !== "") {
          tokens.push({ type: bufferType, value: buffer });
        }
        stringType = undefined;
        bufferType = "symbol";
        buffer = char;
        continue;
      }
      if (bufferType !== "number" && bufferType !== "string" && char === ".") {
        if (buffer !== "") {
          tokens.push({ type: bufferType, value: buffer });
        }
        bufferType = "symbol";
        buffer = char;
        tokens.push({ type: "symbol", value: "." });
        bufferType = undefined;
        buffer = "";
        continue;
      }
      if (
        bufferType !== "object" &&
        bufferType !== "string" &&
        "0123456789".includes(char)
      ) {
        if (bufferType !== "number") {
          bufferType = "number";
          buffer = "";
        }
        stringType = undefined;
        bufferType = "number";
        buffer += char;
        continue;
      }
      if (bufferType === undefined) {
        bufferType = "object";
      }
      buffer += char;
    }
    if (buffer !== "" && bufferType !== "string") {
      tokens.push({ type: bufferType, value: buffer });
    }
    if (bufferType === "string") {
      callbacks.failure(
        this.QuerySyntaxError("unclosed string in query").message,
        0,
        0,
        "AtheneumContactsHelper.query",
      );
      return;
    }

    const filteredTokens = tokens.filter(
      (token) => token.type !== "whitespace",
    );

    // Auto-complete simple queries
    if (
      filteredTokens[0] &&
      filteredTokens[0].type === "keyword" &&
      filteredTokens[0].value === "select" &&
      filteredTokens[1] &&
      filteredTokens[1].type === "object" &&
      filteredTokens[1].value === "Contacts" &&
      filteredTokens[2] &&
      filteredTokens[2].type === "object" &&
      filteredTokens[2].value === "where"
    ) {
      filteredTokens.splice(1, 0, { type: "keyword", value: "from" });
      filteredTokens.splice(1, 0, { type: "symbol", value: "*" });
    }
    if (
      filteredTokens[0] &&
      filteredTokens[0].type === "object" &&
      filteredTokens[1] &&
      filteredTokens[1].type === "keyword" &&
      filteredTokens[1].value === "where"
    ) {
      filteredTokens.unshift(
        { type: "keyword", value: "select" },
        { type: "symbol", value: "*" },
        { type: "keyword", value: "from" },
      );
    }
    if (
      filteredTokens[0] &&
      filteredTokens[0].type === "keyword" &&
      filteredTokens[0].value === "where"
    ) {
      filteredTokens.unshift(
        { type: "keyword", value: "select" },
        { type: "symbol", value: "*" },
        { type: "keyword", value: "from" },
        { type: "object", value: "Contacts" },
      );
    }
    if (
      filteredTokens[0] &&
      filteredTokens[0].type === "keyword" &&
      filteredTokens[0].value === "from"
    ) {
      filteredTokens.unshift(
        { type: "keyword", value: "select" },
        { type: "symbol", value: "*" },
      );
    }
    if (
      filteredTokens[0] &&
      filteredTokens[0].type === "keyword" &&
      filteredTokens[0].value === "select" &&
      filteredTokens[1] &&
      filteredTokens[1].type === "keyword" &&
      filteredTokens[1].value === "from"
    ) {
      filteredTokens.splice(1, 0, { type: "symbol", value: "*" });
    }

    // Basic syntax validation
    if (
      !filteredTokens[0] ||
      filteredTokens[0].type !== "keyword" ||
      filteredTokens[0].value !== "select" ||
      !filteredTokens[1] ||
      (filteredTokens[1].type !== "object" &&
        filteredTokens[1].type !== "symbol") ||
      !filteredTokens[2] ||
      filteredTokens[2].type !== "keyword" ||
      filteredTokens[2].value !== "from" ||
      !filteredTokens[3] ||
      filteredTokens[3].type !== "object"
    ) {
      callbacks.failure(
        this.QuerySyntaxError("syntax error").message,
        0,
        0,
        "AtheneumContactsHelper.query",
      );
      return;
    }

    if (
      !(
        filteredTokens.length === 4 ||
        (filteredTokens[4] &&
          filteredTokens[4].type === "keyword" &&
          (filteredTokens[4].value === "where" ||
            filteredTokens[4].value === "limit" ||
            filteredTokens[4].value === "offset"))
      )
    ) {
      callbacks.failure(
        this.QuerySyntaxError("syntax error").message,
        0,
        0,
        "AtheneumContactsHelper.query",
      );
      return;
    }

    const fieldsToSelect = filteredTokens[1].value;
    let limit = null;
    let offset = 0;
    let whereClause = null;

    for (const token of filteredTokens) {
      if (token.type === "keyword") {
        if (token.value === "limit") {
          if (limit !== null) {
            callbacks.failure(
              this.QuerySyntaxError(
                "multiple instances of keyword 'limit' in query",
              ).message,
              0,
              0,
              "AtheneumContactsHelper.query",
            );
            return;
          }
          const nextToken = filteredTokens[filteredTokens.indexOf(token) + 1];
          if (nextToken === undefined || nextToken.type !== "number") {
            callbacks.failure(
              this.QuerySyntaxError("expected number after keyword 'limit'")
                .message,
              0,
              0,
              "AtheneumContactsHelper.query",
            );
            return;
          }
          limit = parseInt(nextToken.value, 10);
        } else if (token.value === "offset") {
          if (offset !== 0) {
            callbacks.failure(
              this.QuerySyntaxError(
                "multiple instances of keyword 'offset' in query",
              ).message,
              0,
              0,
              "AtheneumContactsHelper.query",
            );
            return;
          }
          const nextToken = filteredTokens[filteredTokens.indexOf(token) + 1];
          if (nextToken === undefined || nextToken.type !== "number") {
            callbacks.failure(
              this.QuerySyntaxError("expected number after keyword 'offset'")
                .message,
              0,
              0,
              "AtheneumContactsHelper.query",
            );
            return;
          }
          offset = parseInt(nextToken.value, 10);
        } else if (token.value === "where") {
          whereClause = filteredTokens
            .slice(filteredTokens.indexOf(token) + 1)
            .filter(
              (t) =>
                t.type !== "keyword" ||
                (t.value !== "limit" && t.value !== "offset"),
            );
          break;
        }
      }
    }

    let dexieCollection = this.db.contacts.toCollection();
    const TRUE_VALUES = ["on", "true", "y", "yes", true];

    if (!TRUE_VALUES.includes(this.Atheneum.User.test)) {
      dexieCollection = dexieCollection.filter(
        (contact) => contact.test !== true,
      );
    }

    if (whereClause && whereClause.length >= 3) {
      const field = whereClause[0].value;
      const operator = whereClause[1].value;
      const value = whereClause[2].value;

      const fieldNameLowerCase = field.toLowerCase();
      const originalDbFieldName =
        this.fieldMap[fieldNameLowerCase] || `info_${fieldNameLowerCase}`;
      const normalizedDbFieldName = this.normalizedFieldMap[fieldNameLowerCase];

      let fieldToQueryOn = originalDbFieldName;
      let valueToQueryWith = value;

      if (operator === "like" || (operator === "=" && normalizedDbFieldName)) {
        if (normalizedDbFieldName) {
          fieldToQueryOn = normalizedDbFieldName;
          valueToQueryWith = this._normalizeStringForSearch(value);
        }
      }

      if (operator === "=") {
        dexieCollection = dexieCollection.filter((contact) => {
          const contactValue = contact[fieldToQueryOn];
          return (
            contactValue !== undefined &&
            this._normalizeStringForSearch(String(contactValue)) ===
              valueToQueryWith
          );
        });
      } else if (operator === "like") {
        const lowerCaseQueryValue = this._normalizeStringForSearch(
          value,
        ).replace(/%/g, "");
        dexieCollection = dexieCollection.filter((contact) => {
          const contactValue = contact[originalDbFieldName];
          if (contactValue === undefined || typeof contactValue !== "string")
            return false;
          return this._normalizeStringForSearch(contactValue).includes(
            lowerCaseQueryValue,
          );
        });
      } else {
        callbacks.failure(
          this.QuerySyntaxError(`unsupported operator '${operator}'`).message,
          0,
          0,
          "AtheneumContactsHelper.query",
        );
        return;
      }
    }

    try {
      let rawContacts = await dexieCollection.toArray();

      if (offset > 0) {
        rawContacts = rawContacts.slice(offset);
      }
      if (limit !== null) {
        rawContacts = rawContacts.slice(0, limit);
      }

      let results = [];
      if (fieldsToSelect !== "*") {
        results = rawContacts
          .map((contactData) => {
            const fieldMapKey = fieldsToSelect.toLowerCase();
            const mappedFieldName =
              this.fieldMap[fieldMapKey] || `info_${fieldMapKey}`;
            return contactData[mappedFieldName] || null;
          })
          .filter((result) => result !== null);
      } else {
        results = rawContacts.map(
          (contactData) =>
            new Atheneum.Contact(this.processContactForRetrieval(contactData)),
        );
      }

      callbacks.success(results);
    } catch (error) {
      console.error("Error executing Dexie.js query:", error);
      callbacks.failure(error.message, 0, 0, "AtheneumContactsHelper.query");
    }
  }

  QuerySyntaxError(message) {
    const error = new Error(message);
    error.name = "QuerySyntaxError";
    error.toString = function () {
      return this.name + ": " + this.message;
    };
    return error;
  }

  /**
   * Asynchronously retrieves contacts by a specific field value.
   * This version performs an initial indexed search and then applies in-memory filters
   * to allow for partial matches across multiple fields in a form.
   */
  async getByFieldValue(
    fieldName,
    fieldValue,
    options,
    formFilterMode = false,
    formObj = null,
    $form = null,
  ) {
    if (!this.db) {
      console.warn("Dexie.js is not available. Cannot retrieve contacts.");
      return [];
    }

    const defaults = {
      exact: fieldName.toLowerCase() === "id",
      consented: undefined,
      limit: null,
      offset: 0,
    };

    const optionsToUseForQuery = { ...defaults, ...options };

    let queryChain;
    const TRUE_VALUES = ["on", "true", "y", "yes", true];
    const userIsTest = TRUE_VALUES.includes(this.Atheneum.User.test);

    const fieldNameLowerCase = fieldName.toLowerCase();
    let dbFieldName =
      this.fieldMap[fieldNameLowerCase] || `info_${fieldNameLowerCase}`;
    let fieldQueryValue = fieldValue;

    // Use an indexed 'startsWith' query for the primary field for performance.
    if (
      !optionsToUseForQuery.exact &&
      this.normalizedFieldMap[fieldNameLowerCase]
    ) {
      dbFieldName = this.normalizedFieldMap[fieldNameLowerCase];
      fieldQueryValue = this._normalizeStringForSearch(fieldValue);
      queryChain = this.db.contacts
        .where(dbFieldName)
        .startsWith(fieldQueryValue);
    } else if (optionsToUseForQuery.exact) {
      queryChain = this.db.contacts.where(dbFieldName).equals(fieldQueryValue);
    } else {
      // Fallback to a full table scan if no suitable index can be used.
      queryChain = this.db.contacts.toCollection();
    }

    let commonFilteredQuery = queryChain.filter((contactData) => {
      if (!userIsTest && contactData.test === true) {
        return false;
      }
      // Apply consent status filter if specified
      if (optionsToUseForQuery.consented !== undefined) {
        const contactIsConsented = contactData.info_consented === true;
        if (optionsToUseForQuery.consented !== contactIsConsented) {
          return false;
        }
      }
      return true;
    });

    let paginatedContacts;

    if (formFilterMode && formObj && $form) {
      let limitedQuery = commonFilteredQuery;
      if (
        this.Atheneum.Theme.getIntegerProperty(
          "contactsFormFilterModeHardLimit",
          10000,
        ) > 0
      ) {
        limitedQuery = commonFilteredQuery.limit(
          this.Atheneum.Theme.getIntegerProperty(
            "contactsFormFilterModeHardLimit",
            10000,
          ),
        );
      }

      // 1. Get all contacts that pass common filters.
      const rawContacts = await limitedQuery.toArray();

      // 2. Final in-memory filtering for complex cases.
      const filteredContacts = rawContacts.filter((contactData) => {
        // (userIsTest and consented checks are already done by commonFilteredQuery)
        for (const fieldObj of formObj.fields) {
          const key = fieldObj.name;
          if (key.toLowerCase() === fieldNameLowerCase) continue;

          const fieldElement = $form.find(
            `input[name="${key}"], select[name="${key}"], textarea[name="${key}"]`,
          );
          if (fieldElement.length === 0) continue;

          const actualField = fieldElement[0];
          if (
            actualField.type !== "text" &&
            actualField.type !== "select-one" &&
            actualField.type !== "textarea"
          )
            continue;

          const val = $(actualField).val();
          if (val && val.length > 0) {
            const filterDbFieldName =
              this.fieldMap[key.toLowerCase()] || `info_${key}`;
            const normalizedContactValue = this._normalizeStringForSearch(
              contactData[filterDbFieldName],
            );
            const normalizedFilterValue = this._normalizeStringForSearch(val);

            if (!normalizedContactValue.includes(normalizedFilterValue)) {
              return false;
            }
          }
        }
        return true;
      });

      // 3. Apply pagination in-memory using .slice()
      paginatedContacts = filteredContacts.slice(
        optionsToUseForQuery.offset || 0,
        (optionsToUseForQuery.offset || 0) +
          (optionsToUseForQuery.limit || Infinity),
      );
    } else {
      // We can apply pagination at the DB level.
      if (optionsToUseForQuery.offset > 0) {
        commonFilteredQuery = commonFilteredQuery.offset(
          optionsToUseForQuery.offset,
        );
      }
      if (optionsToUseForQuery.limit) {
        commonFilteredQuery = commonFilteredQuery.limit(
          optionsToUseForQuery.limit,
        );
      }

      // 1. Get only the paginated results from Dexie.
      paginatedContacts = await commonFilteredQuery.toArray();
    }

    // Apply pagination and then map to the final contact objects.
    return paginatedContacts.map(
      (contactData) =>
        new Atheneum.Contact(this.processContactForRetrieval(contactData)),
    );
  }

  async getById(id, options) {
    if (!this.db) return [];

    const contactData = await this.db.contacts.get(id);

    if (contactData) {
      const TRUE_VALUES = ["on", "true", "y", "yes", true];

      if (
        !TRUE_VALUES.includes(this.Atheneum.User.test) &&
        contactData.test === true
      ) {
        return [];
      }

      const contact = new Atheneum.Contact(
        this.processContactForRetrieval(contactData),
      );
      if (options && options.consented !== undefined) {
        if (
          (options.consented === true && !contact.isConsented()) ||
          (options.consented === false && contact.isConsented())
        ) {
          return [];
        }
      }
      return [contact];
    }
    return [];
  }

  async getByFirstName(firstName, options) {
    return await this.getByFieldValue("firstName", firstName, options);
  }

  async getByLastName(lastName, options) {
    return await this.getByFieldValue("lastName", lastName, options);
  }

  async getByEmailAddress(emailAddress, options) {
    return await this.getByFieldValue("email", emailAddress, options);
  }
}
